import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Col,
  Collapse,
  DatePicker,
  Input,
  Row,
  Space,
  theme,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useResumeStore } from "../../store/resumeStore";
import {
  FIELD_SPELLCHECK_PROPS,
} from "./resumeQuality";

const { Panel } = Collapse;
const { useToken } = theme;
const DATE_FORMAT = "DD/MM/YYYY";
const LEGACY_DATE_FORMATS = [
  DATE_FORMAT,
  "D/M/YYYY",
  "DD-MM-YYYY",
  "D-M-YYYY",
  "YYYY-MM-DD",
  "MMM YYYY",
  "MMMM YYYY",
  "MM/YYYY",
  "M/YYYY",
  "YYYY",
];

dayjs.extend(customParseFormat);

function parseEditorDate(value) {
  if (typeof value !== "string" || !value.trim()) return null;

  const parsed = dayjs(value.trim(), LEGACY_DATE_FORMATS, true);
  return parsed.isValid() ? parsed : null;
}

function getDatePlaceholder(value, fallback) {
  if (parseEditorDate(value)) return fallback;
  return typeof value === "string" && value.trim() ? value : fallback;
}

function buildLocalDescriptions(entries = []) {
  const nextValue = {};
  entries.forEach((entry, index) => {
    nextValue[index] = entry?.description || "";
  });
  return nextValue;
}

export default function ExperienceEditor() {
  const { token } = useToken();

  /* =============================
     ZUSTAND
  ============================== */

  const experience = useResumeStore((s) => s.resume.experience);
  const updateResume = useResumeStore((s) => s.updateResume);

  /* =============================
     LOCAL QUILL STATE
     (Prevents infinite loop)
  ============================== */
  const [localDescriptions, setLocalDescriptions] = useState(() =>
    buildLocalDescriptions(experience),
  );
  const quillRefs = useRef([]);

  /* Sync local state when Zustand updates */
  useEffect(() => {
    setLocalDescriptions(buildLocalDescriptions(experience));
  }, [experience]);

  useEffect(() => {
    quillRefs.current.forEach((instance) => {
      const editorRoot = instance?.getEditor?.()?.root;
      if (!editorRoot) return;

      editorRoot.setAttribute("spellcheck", "true");
      editorRoot.setAttribute("autocorrect", "on");
      editorRoot.setAttribute("autocapitalize", "sentences");
    });
  }, [localDescriptions, experience.length]);

  /* =============================
     UPDATE EXPERIENCE
  ============================== */

  const updateExperience = (index, field, value) => {
    if (experience[index]?.[field] === value) return;

    const newExp = [...experience];
    const nextExperience = {
      ...newExp[index],
      [field]: value,
    };

    if (field === "startDate" || field === "endDate") {
      nextExperience.years = "";
    }

    newExp[index] = nextExperience;

    updateResume("experience", newExp);
  };

  /* =============================
     ADD / REMOVE
  ============================== */

  const addExperience = () => {
    updateResume("experience", [
      ...experience,
      {
        company: "",
        role: "",
        project: "",
        projectDisplayMode: "",
        startDate: "",
        endDate: "",
        years: "",
        description: "",
      },
    ]);
  };

  const removeExperience = (index) => {
    updateResume(
      "experience",
      experience.filter((_, i) => i !== index)
    );
  };

  /* =============================
     QUILL CONFIG
  ============================== */

  const quillModules = {
    toolbar: [
      ["bold", "italic"],
      [{ background: ["yellow", "red", "green", "blue"] }],
      [{ list: "bullet" }],
      ["clean"],
    ],
  };

  const quillFormats = ["bold", "italic", "background", "list", "bullet"];
  const quillEditorStyle = { height: 180, marginBottom: 42 };

  /* =============================
     HANDLE QUILL CHANGE
  ============================== */

  const handleQuillChange = (index, value) => {

    // Update local state first
    setLocalDescriptions((prev) => ({
      ...prev,
      [index]: value,
    }));

    // Sync to Zustand only if changed
    if (value !== experience[index]?.description) {
      updateExperience(index, "description", value);
    }
  };

  return (
    <div>
      <h3 style={{ color: token.colorTextHeading, marginBottom: 16 }}>
        Experience
      </h3>

      <Collapse
        accordion
        ghost
        expandIconPosition="end"
        defaultActiveKey={experience?.length ? ["0"] : []}
      >
        {experience?.map((exp, index) => (
          <Panel
            key={index}
            header={exp.company || `Experience ${index + 1}`}
            extra={
              <DeleteOutlined
                style={{ color: token.colorError }}
                onClick={(e) => {
                  e.stopPropagation();
                  removeExperience(index);
                }}
              />
            }
          >
            <Space direction="vertical" style={{ width: "100%" }} size={12}>
              <Input
                placeholder="Company Name"
                value={exp.company}
                onChange={(e) =>
                  updateExperience(index, "company", e.target.value)
                }
                {...FIELD_SPELLCHECK_PROPS}
              />

              <Input
                placeholder="Role"
                value={exp.role}
                onChange={(e) =>
                  updateExperience(index, "role", e.target.value)
                }
                {...FIELD_SPELLCHECK_PROPS}
              />

              <Input
                placeholder="Project"
                value={exp.project}
                onChange={(e) =>
                  updateExperience(index, "project", e.target.value)
                }
                {...FIELD_SPELLCHECK_PROPS}
              />

              <Row gutter={[12, 12]}>
                <Col xs={24} sm={12}>
                  <DatePicker
                    style={{ width: "100%" }}
                    format={DATE_FORMAT}
                    placeholder={getDatePlaceholder(
                      exp.startDate,
                      "Start Date (DD/MM/YYYY)",
                    )}
                    value={parseEditorDate(exp.startDate)}
                    onChange={(_, dateString) =>
                      updateExperience(index, "startDate", dateString || "")
                    }
                  />
                </Col>

                <Col xs={24} sm={12}>
                  <DatePicker
                    style={{ width: "100%" }}
                    format={DATE_FORMAT}
                    placeholder={getDatePlaceholder(
                      exp.endDate,
                      "End Date (DD/MM/YYYY)",
                    )}
                    value={parseEditorDate(exp.endDate)}
                    onChange={(_, dateString) =>
                      updateExperience(index, "endDate", dateString || "")
                    }
                  />
                </Col>
              </Row>

              <ReactQuill
                ref={(instance) => {
                  quillRefs.current[index] = instance;
                }}
                theme="snow"
                value={
                  Object.prototype.hasOwnProperty.call(localDescriptions, index)
                    ? localDescriptions[index]
                    : exp.description || ""
                }
                modules={quillModules}
                formats={quillFormats}
                onChange={(value) => handleQuillChange(index, value)}
                style={quillEditorStyle}
              />
            </Space>
          </Panel>
        ))}
      </Collapse>

      <Button
        type="dashed"
        icon={<PlusOutlined />}
        block
        style={{ marginTop: 16 }}
        onClick={addExperience}
      >
        Add Experience
      </Button>
    </div>
  );
}
